import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AudioRecorder from '../audio/AudioRecorder';
import AvatarLightbox from './AvatarLightbox';
import api from '../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import './ChatWindow.css';

/**
 * Formata data para separador: "Hoje", "Ontem", ou "DD/MM/YYYY"
 */
function formatDateSeparator(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (isSameDay(date, today)) return 'Hoje';
  if (isSameDay(date, yesterday)) return 'Ontem';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Retorna a chave de data (YYYY-MM-DD) para agrupar mensagens.
 */
function getDateKey(dateStr) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Agrupa mensagens por data, inserindo separadores.
 * Retorna array de { type: 'separator', date, label } e { type: 'message', ...msg }
 */
function groupMessagesByDate(messages) {
  const items = [];
  let lastDateKey = null;

  for (const msg of messages) {
    const dateKey = getDateKey(msg.created_at);
    if (dateKey !== lastDateKey) {
      items.push({
        type: 'separator',
        key: `sep-${dateKey}`,
        label: formatDateSeparator(msg.created_at),
      });
      lastDateKey = dateKey;
    }
    items.push({ type: 'message', ...msg });
  }

  return items;
}

/**
 * Converte URLs no texto em links clicáveis React.
 * Suporta: http(s)://, www., booking.com/Share-xxx
 */
function linkifyContent(text, isOutbound) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  const parts = text.split(urlRegex);
  
  if (parts.length === 1) return text; // sem URLs
  
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      // Reset lastIndex (global flag)
      urlRegex.lastIndex = 0;
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`message-link ${isOutbound ? 'outbound-link' : 'inbound-link'}`}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function ChatWindow({ conversation, messages, onSendMessage, hasMore, loadingMore, onLoadMore, onContactClick, onBack }) {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // { file, previewUrl, isImage }
  const [sendingFile, setSendingFile] = useState(false); // proteção contra clique duplo
  const [forwardMsg, setForwardMsg] = useState(null);   // mensagem a encaminhar
  const [contextMenu, setContextMenu] = useState(null); // { x, y, message }
  const [contactTasks, setContactTasks] = useState([]);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [showTaskCreate, setShowTaskCreate] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const messageRefs = useRef({});
  const topSentinelRef = useRef(null);
  const fileInputRef = useRef(null);
  const taskDropdownRef = useRef(null);
  const isInitialLoad = useRef(true);
  const prevMessagesLen = useRef(0);
  const prevScrollHeight = useRef(0);

  // Fetch tasks for current contact
  const fetchContactTasks = useCallback(async () => {
    if (!conversation?.contact?.id) return;
    try {
      const data = await api.getTasks({ contactId: conversation.contact.id, status: 'active' });
      setContactTasks(data);
    } catch { }
  }, [conversation?.contact?.id]);

  useEffect(() => {
    fetchContactTasks();
  }, [fetchContactTasks]);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightboxUrl) return;
    const handler = (e) => { if (e.key === 'Escape') setLightboxUrl(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxUrl]);

  // Fechar menu de contexto ao clicar fora ou pressionar Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    const handleKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [contextMenu]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showTaskDropdown) return;
    const handler = (e) => {
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(e.target)) {
        setShowTaskDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTaskDropdown]);

  /**
   * Verifica se o usuário está perto do final do chat.
   * Threshold de 150px — se está a menos de 150px do fundo, considera "no final".
   */
  const isNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    const threshold = 150;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  // Scroll to bottom: apenas no load inicial OU quando usuário já está no final
  useEffect(() => {
    if (!containerRef.current) return;

    const isInitial = isInitialLoad.current;
    const messagesAppended = messages.length > prevMessagesLen.current &&
      prevMessagesLen.current > 0 &&
      !isInitial;

    // Prepend (loadMore) → preservar posição (tratado abaixo)
    const messagesPrepended = messages.length > prevMessagesLen.current &&
      prevMessagesLen.current > 0 &&
      prevScrollHeight.current > 0;

    if (messagesPrepended) {
      // Manter posição de scroll após prepend de mensagens antigas
      const newScrollHeight = containerRef.current.scrollHeight;
      const diff = newScrollHeight - prevScrollHeight.current;
      if (diff > 0) {
        containerRef.current.scrollTop += diff;
      }
      prevScrollHeight.current = 0; // Reset — só usa uma vez
    } else if (isInitial) {
      // Primeiro load: vai direto pro final
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'auto' });
        isInitialLoad.current = false;
      });
    } else if (messagesAppended && isNearBottom()) {
      // Nova mensagem E usuário está perto do final → scroll suave
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
    // Se usuário está lendo histórico (não está no final) → NÃO mexe no scroll

    prevMessagesLen.current = messages.length;
  }, [messages, isNearBottom]);

  // Reset on conversation change
  useEffect(() => {
    isInitialLoad.current = true;
    prevMessagesLen.current = 0;
    prevScrollHeight.current = 0;
    setReplyingTo(null);
  }, [conversation?.id]);

  // Intersection Observer for scroll-up loading
  useEffect(() => {
    if (!topSentinelRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          // Save scroll height before loading
          if (containerRef.current) {
            prevScrollHeight.current = containerRef.current.scrollHeight;
          }
          onLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin: '100px 0px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(topSentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const handleSend = async () => {
    if (!inputText.trim()) return;
    
    await onSendMessage({
      content: inputText,
      type: 'text',
      quoted_message_id: replyingTo?.message_key_id || null
    });
    
    setInputText('');
    setReplyingTo(null);
  };

  const handleAudioReady = async (audioData) => {
    await onSendMessage({
      content: '🎤 Áudio',
      type: 'audio',
      media_url: audioData.url,
      media_type: 'audio',
      media_filename: audioData.filename,
      media_mimetype: audioData.mimetype
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const isImage = file.type.startsWith('image/');
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    setPendingFile({ file, previewUrl, isImage });
  };

  const handleFileSend = async () => {
    if (!pendingFile || sendingFile) return;
    setSendingFile(true);
    const { file, isImage } = pendingFile;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';
      const resp = await fetch(`${API_URL}/media/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!resp.ok) throw new Error(`Upload falhou: ${resp.status}`);
      const data = await resp.json();
      // Garantir mimetype correto — nunca confiar só no browser
      const mimetype = data.mimetype && data.mimetype !== 'false'
        ? data.mimetype
        : (isImage ? 'image/jpeg' : 'application/pdf');
      await onSendMessage({
        content: isImage ? 'Imagem enviada' : `📎 ${file.name}`,
        type: isImage ? 'image' : 'document',
        media_url: data.url,
        media_type: isImage ? 'image' : 'document',
        media_filename: file.name,
        media_mimetype: mimetype,
        quoted_message_id: replyingTo?.message_key_id || null,
      });
      setPendingFile(null);
      setReplyingTo(null);
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err);
    } finally {
      setSendingFile(false);
    }
  };

  // ─── Task actions ───
  const handleCompleteTask = async (taskId) => {
    try {
      await api.updateTask(taskId, { status: 'done' });
      fetchContactTasks();
    } catch (e) { console.error(e); }
  };

  const handleTaskCreated = () => {
    setShowTaskCreate(false);
    fetchContactTasks();
  };

  const pendingTasks = contactTasks.filter(t => t.status === 'pending');
  const waitingTasks = contactTasks.filter(t => t.status === 'waiting');
  const taskBadge = pendingTasks.length + waitingTasks.length;
  const today = new Date().toISOString().split('T')[0];
  const hasOverdue = pendingTasks.some(t => t.due_date && t.due_date < today);

  // Agrupar mensagens por data
  const groupedItems = groupMessagesByDate(messages);

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-header">
        <div className="contact-info">
          {onBack && (
            <button
              onClick={onBack}
              className="mobile-back-btn"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 4px 8px 0',
                marginRight: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#090777',
                minWidth: '32px',
                minHeight: '44px'
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          )}
          <AvatarLightbox
            src={conversation.contact.avatar_url}
            alt={conversation.contact.name}
            className="avatar"
            fallback={
              <div className="avatar-placeholder">
                {conversation.contact.name[0]}
              </div>
            }
          />
          <div>
            <h3
              className="contact-name-link"
              onClick={() => onContactClick?.(conversation.contact)}
            >{conversation.contact.name}</h3>
            <p className="phone">{conversation.contact.phone}</p>
          </div>
        </div>
        <div className="header-actions">
          {/* Botão Cotar — abre assistente de cotação vinculado a este contato */}
          <button
            className="task-add-btn cotar-btn"
            onClick={() => navigate(`/app/assistente?contact_id=${conversation.contact.id}`)}
            title="Nova cotação"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', fontSize: '0.75rem', fontWeight: 500,
              color: '#fff', backgroundColor: '#50CFAD',
              border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '14px' }}>✈</span>
            <span>+ Cotar</span>
          </button>
          {/* Botão Voucher — abre fluxo de vouchers vinculado a este contato */}
          <button
            className="task-add-btn voucher-btn"
            onClick={() => navigate(`/app/vouchers?contact_id=${conversation.contact.id}`)}
            title="Adicionar voucher"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', fontSize: '0.75rem', fontWeight: 500,
              color: '#fff', backgroundColor: '#09077D',
              border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '14px' }}>🎟️</span>
            <span>+ Voucher</span>
          </button>
          {/* Task badge + dropdown */}
          <div className="task-header-wrapper" ref={taskDropdownRef}>
            {taskBadge > 0 && (
              <button
                className={`task-badge-btn ${hasOverdue ? 'overdue' : ''}`}
                onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                title="Ver tarefas"
              >
                📋 {taskBadge}
              </button>
            )}
            <button
              className="task-add-btn"
              onClick={() => { setShowTaskCreate(!showTaskCreate); setShowTaskDropdown(false); }}
              title="Nova tarefa"
            >
              📋+
            </button>

            {/* Dropdown */}
            {showTaskDropdown && (
              <div className="task-dropdown">
                <div className="task-dropdown-title">
                  Tarefas de {conversation.contact.name}
                </div>
                {contactTasks.length === 0 ? (
                  <div className="task-dropdown-empty">Nenhuma tarefa</div>
                ) : (
                  contactTasks.slice(0, 5).map(t => (
                    <div key={t.id} className={`task-dropdown-item ${t.status === 'waiting' ? 'waiting' : ''}`}>
                      <button
                        className="mini-checkbox"
                        onClick={() => handleCompleteTask(t.id)}
                      >
                        {t.status === 'waiting' ? '⏸' : '☐'}
                      </button>
                      <span className="mini-title">{t.title}</span>
                      {t.due_date && (
                        <span className={`mini-date ${t.due_date < today ? 'overdue' : ''}`}>
                          {t.due_date === today ? 'Hoje' : new Date(t.due_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        </span>
                      )}
                    </div>
                  ))
                )}
                {contactTasks.length > 5 && (
                  <div className="task-dropdown-more">
                    Ver todas →
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task creation drawer */}
      {showTaskCreate && (
        <div className="task-create-drawer">
          <ChatTaskCreate
            contactId={conversation.contact.id}
            contactName={conversation.contact.name}
            conversationId={conversation.id}
            onCreated={handleTaskCreated}
            onCancel={() => setShowTaskCreate(false)}
          />
        </div>
      )}

      {/* Messages */}
      <div className="messages-container" ref={containerRef}>
        {/* Sentinel para scroll-up */}
        <div ref={topSentinelRef} className="scroll-sentinel" />

        {/* Loading indicator */}
        {loadingMore && (
          <div className="loading-more">
            <div className="loading-spinner" />
            <span>Carregando mensagens...</span>
          </div>
        )}

        {hasMore && !loadingMore && messages.length > 0 && (
          <div className="loading-more hint">
            <span>↑ Role para cima para ver mensagens anteriores</span>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="empty-messages">
            <p>Nenhuma mensagem ainda</p>
          </div>
        ) : (
          groupedItems.map((item) => {
            if (item.type === 'separator') {
              return (
                <div key={item.key} className="date-separator">
                  <span>{item.label}</span>
                </div>
              );
            }

            return (
              <div
                key={item.id}
                ref={el => { if (el) messageRefs.current[item.message_key_id || item.id] = el; }}
                className={`message ${item.direction}`}
              >
                <div className="message-bubble">
                  {/* Citação (mensagem respondida ou reagida) */}
                  {(() => {
                    const isReaction = item.content?.startsWith('Reação:');
                    let resolvedContent = item.quoted_content;
                    let resolvedSender = item.quoted_sender;
                    let resolvedMediaUrl = item.quoted_media_url;
                    let resolvedMediaType = item.quoted_media_type;

                    // Para reações ou quando quoted_content ainda não veio do banco:
                    // buscar a mensagem original localmente pelo message_key_id
                    if (item.quoted_message_id && !resolvedContent) {
                      const original = messages.find(
                        m => m.message_key_id === item.quoted_message_id
                      );
                      if (original) {
                        resolvedContent = original.content || '📎 Mídia';
                        resolvedSender = original.direction === 'inbound'
                          ? (conversation.contact?.name || 'Cliente')
                          : 'Você';
                        resolvedMediaUrl = original.media_url;
                        resolvedMediaType = original.media_type;
                      }
                    }

                    if (!resolvedContent && !resolvedMediaUrl) return null;

                    const scrollToOriginal = () => {
                      if (!item.quoted_message_id) return;
                      const el = messageRefs.current[item.quoted_message_id];
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el.classList.add('message-highlight');
                        setTimeout(() => el.classList.remove('message-highlight'), 1200);
                      }
                    };

                    return (
                      <div
                        className={`quoted-preview ${item.direction === 'outbound' ? 'quoted-outbound' : 'quoted-inbound'}`}
                        onClick={scrollToOriginal}
                        style={{ cursor: item.quoted_message_id ? 'pointer' : 'default' }}
                      >
                        {resolvedSender && (
                          <span className="quoted-sender">{resolvedSender}</span>
                        )}
                        {resolvedMediaType === 'image' && resolvedMediaUrl && (
                          <img
                            src={resolvedMediaUrl}
                            alt=""
                            className="quoted-image-thumb"
                          />
                        )}
                        {resolvedMediaType === 'document' && (
                          <span className="quoted-doc-icon">📄</span>
                        )}
                        {resolvedMediaType === 'audio' && (
                          <span className="quoted-doc-icon">🎤</span>
                        )}
                        <span className="quoted-text">{resolvedContent}</span>
                      </div>
                    );
                  })()}

                  {/* Imagem */}
                  {item.media_type === 'image' && item.media_url && (
                    <img
                      src={item.media_url}
                      alt=""
                      className="message-image"
                      onClick={() => setLightboxUrl(item.media_url)}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                      }}
                    />
                  )}

                  {/* Áudio */}
                  {item.media_type === 'audio' && item.media_url && (
                    <audio controls preload="none" className="message-audio">
                      <source src={item.media_url} type="audio/ogg" />
                      <source src={item.media_url} type="audio/webm" />
                      Seu navegador não suporta áudio.
                    </audio>
                  )}

                  {/* Documento */}
                  {item.media_type === 'document' && item.media_url && (
                    <a
                      href={item.media_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="message-document"
                    >
                      <span className="document-icon">📄</span>
                      <span className="document-name">{item.media_filename || 'Documento'}</span>
                    </a>
                  )}

                  {/* Vídeo */}
                  {item.media_type === 'video' && item.media_url && (
                    <video controls preload="none" className="message-video">
                      <source src={item.media_url} />
                    </video>
                  )}
                  
                  {/* Conteúdo texto — COM LINKS CLICÁVEIS */}
                  {item.content && !(item.media_type === 'image' && item.content === 'Imagem enviada') && !(item.media_type === 'audio' && item.content === '🎤 Áudio') && (
                    <p style={{ whiteSpace: 'pre-wrap' }}>{linkifyContent(item.content, item.direction === 'outbound')}</p>
                  )}
                  
                  <span className="message-time">
                    {new Date(item.created_at).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>

                  {/* Seta de menu — aparece no hover, igual WhatsApp */}
                  <button
                    className="msg-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setContextMenu({ x: rect.left, y: rect.bottom + 4, message: item });
                    }}
                    title="Mais opções"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
                  </button>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply bar (respondendo a...) */}
      {replyingTo && (
        <div className="reply-bar">
          <div className="reply-bar-content">
            <div className="reply-bar-accent" />
            <div className="reply-bar-info">
              <span className="reply-bar-sender">
                {replyingTo.direction === 'outbound' ? 'Você' : (conversation.contact?.name || 'Cliente')}
              </span>
              <span className="reply-bar-text">{replyingTo.content?.substring(0, 100)}</span>
            </div>
          </div>
          <button className="reply-bar-close" onClick={() => setReplyingTo(null)}>✕</button>
        </div>
      )}

      {/* Agent suggestion */}
      {conversation.agent_suggested_reply && !conversation.agent_suggestion_used && (
        <div className="agent-suggestion">
          <p className="agent-suggestion-text">{conversation.agent_suggested_reply}</p>
          <button
            className="agent-suggestion-btn"
            onClick={() => {
              setInputText(conversation.agent_suggested_reply);
            }}
          >
            Usar
          </button>
        </div>
      )}

      {/* Input - WhatsApp style */}
      <div className="chat-input">
        <button className="btn-attach" title="Anexar" onClick={() => fileInputRef.current?.click()}>
          <svg viewBox="0 0 24 24" width="24" height="24"><path fill="#54656f" d="M1.816 15.556v.002c0 1.502.584 2.912 1.646 3.972s2.472 1.647 3.974 1.647a5.58 5.58 0 003.972-1.645l9.547-9.548c.769-.768 1.147-1.767 1.058-2.817-.079-.968-.548-1.927-1.319-2.698-1.594-1.592-4.068-1.711-5.517-.262l-7.916 7.915c-.881.881-.792 2.25.214 3.261.501.501 1.134.786 1.776.786.472 0 .884-.166 1.214-.507l5.749-5.75c.241-.241.241-.631 0-.872-.241-.241-.632-.241-.873 0l-5.75 5.75c-.094.095-.214.133-.373.133-.289 0-.643-.151-.955-.463-.625-.626-.705-1.37-.192-1.882l7.916-7.915c.955-.955 2.68-.876 3.85.293.553.553.902 1.233.956 1.864.057.659-.196 1.305-.709 1.818l-9.547 9.549a4.38 4.38 0 01-3.119 1.291c-1.18 0-2.287-.459-3.119-1.291s-1.292-1.939-1.292-3.119.46-2.287 1.292-3.119l7.56-7.561c.241-.241.241-.631 0-.872-.241-.241-.631-.241-.872 0l-7.56 7.56A5.58 5.58 0 001.816 15.556z"></path></svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <div className="input-wrapper">
          <textarea
            rows={1}
            placeholder="Mensagem"
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
                e.target.style.height = 'auto';
              }
            }}
          />
        </div>
        {inputText.trim() ? (
          <button className="btn-send-round" onClick={handleSend} title="Enviar">
            <svg viewBox="0 0 24 24" width="24" height="24"><path fill="white" d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path></svg>
          </button>
        ) : (
          <AudioRecorder 
            onAudioReady={handleAudioReady}
            disabled={!conversation}
          />
        )}
      </div>
      {/* Lightbox */}
      {lightboxUrl && (
        <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
          <div className="lightbox-toolbar">
            <a
              href={lightboxUrl}
              download
              className="lightbox-btn"
              title="Baixar"
              onClick={(e) => e.stopPropagation()}
            >
              ⬇
            </a>
            <button className="lightbox-btn" onClick={() => setLightboxUrl(null)} title="Fechar">
              ✕
            </button>
          </div>
          <img
            src={lightboxUrl}
            alt=""
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Menu de contexto (estilo WhatsApp) */}
      <MessageContextMenu
        menu={contextMenu}
        onClose={() => setContextMenu(null)}
        onReply={(msg) => setReplyingTo(msg)}
        onForward={(msg) => setForwardMsg(msg)}
        onCopy={() => {}}
      />

      {/* Preview antes de enviar (estilo WhatsApp) */}
      {pendingFile && (
        <div className="file-preview-overlay" onClick={() => { setPendingFile(null); if (pendingFile.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl); }}>
          <div className="file-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="file-preview-header">
              <button className="file-preview-close" onClick={() => { setPendingFile(null); if (pendingFile.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl); }}>✕</button>
              <span className="file-preview-name">{pendingFile.file.name}</span>
            </div>
            <div className="file-preview-body">
              {pendingFile.isImage ? (
                <img src={pendingFile.previewUrl} alt="" className="file-preview-image" />
              ) : (
                <div className="file-preview-doc">
                  <span className="file-preview-doc-icon">📄</span>
                  <span className="file-preview-doc-name">{pendingFile.file.name}</span>
                  <span className="file-preview-doc-size">{(pendingFile.file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>
            <div className="file-preview-footer">
              <button className="file-preview-send" onClick={handleFileSend} disabled={sendingFile} style={{ opacity: sendingFile ? 0.5 : 1 }}>
                {sendingFile
                  ? <div style={{ width: 20, height: 20, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  : <svg viewBox="0 0 24 24" width="24" height="24"><path fill="white" d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"/></svg>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal encaminhar mensagem */}
      {forwardMsg && (
        <ForwardModal
          message={forwardMsg}
          currentConversationId={conversation.id}
          onClose={() => setForwardMsg(null)}
          onSendMessage={onSendMessage}
        />
      )}
    </div>
  );
}

/* ─── Compact task create for chat ─── */
function ChatTaskCreate({ contactId, contactName, conversationId, onCreated, onCancel }) {
  const { profile } = useAuth();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [priority, setPriority] = useState('normal');
  const [assignedTo, setAssignedTo] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { api.getSellers().then(setSellers).catch(() => setSellers([])); }, []);

  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.createTask({
        title: title.trim(),
        due_date: dueDate || null,
        priority,
        contact_id: contactId,
        conversation_id: conversationId,
        seller_id: 'default',
        assigned_to: assignedTo || profile?.seller_id || null,
        created_by: profile?.seller_id || null,
        attachments: attachments.length > 0 ? attachments : null,
      });
      onCreated();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    if (e.key === 'Escape') onCancel();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const result = await api.uploadFile(file);
        setAttachments(prev => [...prev, {
          url: result.url,
          filename: file.name,
          mimetype: result.mimetype,
          size: result.size,
        }]);
      }
    } catch (err) { console.error('Erro no upload:', err); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="chat-task-create">
      <input
        ref={inputRef}
        type="text"
        placeholder="O que precisa fazer?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        className="chat-task-title"
      />
      <div className="chat-task-options">
        <button className={`chip-sm ${dueDate === todayStr ? 'selected' : ''}`} onClick={() => setDueDate(todayStr)}>Hoje</button>
        <button className={`chip-sm ${dueDate === tomorrowStr ? 'selected' : ''}`} onClick={() => setDueDate(tomorrowStr)}>Amanhã</button>
        <input type="date" className="chip-sm" value={dueDate || ''} onChange={(e) => setDueDate(e.target.value)} />
        <span className="chip-divider">|</span>
        <button className={`chip-sm ${priority === 'medium' ? 'selected' : ''}`} onClick={() => setPriority(priority === 'medium' ? 'normal' : 'medium')}>🟡</button>
        <button className={`chip-sm ${priority === 'high' ? 'selected' : ''}`} onClick={() => setPriority(priority === 'high' ? 'normal' : 'high')}>🔴</button>
        <span className="chip-divider">|</span>
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }} />
        <button className={`chip-sm`} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? '⏳' : '📎'}
        </button>
      </div>
      {attachments.length > 0 && (
        <div className="chat-task-attachments">
          {attachments.map((att, i) => (
            <div key={i} className="chat-task-att-item">
              <span>📄 {att.filename}</span>
              <button onClick={() => removeAttachment(i)}>×</button>
            </div>
          ))}
        </div>
      )}
      {sellers.filter(s => s.id !== profile?.seller_id).length > 0 && (
        <div className="chat-task-sellers">
          <div className="chat-task-seller-row">
            <span className="chip-label-sm">📋 Para:</span>
            {sellers.filter(s => s.id !== profile?.seller_id).map(s => (
              <button
                key={s.id}
                className={`chip-sm ${assignedTo === s.id ? 'selected' : ''}`}
                style={assignedTo === s.id ? { backgroundColor: s.color, color: '#fff', borderColor: s.color } : {}}
                onClick={() => setAssignedTo(assignedTo === s.id ? null : s.id)}
              >
                {s.name.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="chat-task-footer">
        <span className="chat-task-contact">👤 {contactName}</span>
        <div className="chat-task-btns">
          <button className="btn-task-cancel" onClick={onCancel}>Cancelar</button>
          <button className="btn-task-confirm" onClick={handleSubmit} disabled={!title.trim() || submitting}>Criar</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Context Menu (estilo WhatsApp) ─── */
function MessageContextMenu({ menu, onClose, onReply, onForward, onCopy }) {
  if (!menu) return null;
  const { x, y, message } = menu;

  // Ajustar posição para não sair da tela
  const menuWidth = 200;
  const menuHeight = 180;
  const adjX = Math.min(x, window.innerWidth - menuWidth - 8);
  const adjY = Math.min(y, window.innerHeight - menuHeight - 8);

  return (
    <div
      className="ctx-menu"
      style={{ top: adjY, left: adjX }}
      onMouseDown={e => e.stopPropagation()}
    >
      <button className="ctx-item" onClick={() => { onReply(message); onClose(); }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17H4V12"/><path d="M4 17L10.929 10.071C11.905 9.095 13.095 9.095 14.071 10.071L20 16"/></svg>
        Responder
      </button>
      <button className="ctx-item" onClick={() => { onForward(message); onClose(); }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 17H20V12"/><path d="M20 17L13.071 10.071C12.095 9.095 10.905 9.095 9.929 10.071L4 16"/></svg>
        Encaminhar
      </button>
      {message.content && (
        <button className="ctx-item" onClick={() => { navigator.clipboard?.writeText(message.content); onClose(); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
          Copiar
        </button>
      )}
    </div>
  );
}

/* ─── Forward Modal (estilo WhatsApp) ─── */
function ForwardModal({ message, currentConversationId, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState([]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';
    fetch(`${API_URL}/conversations?seller_id=all&include_archived=1`)
      .then(r => r.json())
      .then(data => setConversations(data.conversations || data || []))
      .catch(() => {});
  }, []);

  const filtered = conversations.filter(c =>
    c.id !== currentConversationId &&
    (
      (c.contact?.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.contact?.display_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.contact?.phone || '').includes(search)
    )
  );

  const handleForward = async (targetConv) => {
    if (sending || sent.includes(targetConv.id)) return;
    setSending(targetConv.id);
    try {
      const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';
      await fetch(`${API_URL}/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: targetConv.id,
          content: message.content || '',
          media_url: message.media_url || null,
          media_type: message.media_type || null,
          media_filename: message.media_filename || null,
          media_mimetype: message.media_mimetype || null,
        }),
      });
      setSent(prev => [...prev, targetConv.id]);
    } catch (err) {
      console.error('Erro ao encaminhar:', err);
    } finally {
      setSending(null);
    }
  };

  const getInitial = (conv) => {
    const name = conv.contact?.display_name || conv.contact?.name || conv.contact?.phone || '?';
    return name[0].toUpperCase();
  };

  const getColor = (str) => {
    const colors = ['#25D366','#128C7E','#075E54','#34B7F1','#00BCD4'];
    let hash = 0;
    for (let i = 0; i < (str||'').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="fwd-overlay" onClick={onClose}>
      <div className="fwd-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="fwd-header">
          <button className="fwd-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          </button>
          <span className="fwd-title">Encaminhar para</span>
        </div>

        {/* Busca */}
        <div className="fwd-search-wrap">
          <svg className="fwd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8696a0" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input
            className="fwd-search"
            placeholder="Pesquisar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Lista */}
        <div className="fwd-list">
          {filtered.length === 0 && (
            <div className="fwd-empty">Nenhum contato encontrado</div>
          )}
          {filtered.map(c => {
            const name = c.contact?.display_name || c.contact?.name || c.contact?.phone || '?';
            const phone = c.contact?.phone || '';
            const isSent = sent.includes(c.id);
            const isSending = sending === c.id;
            return (
              <div key={c.id} className="fwd-item" onClick={() => !isSent && handleForward(c)}>
                <div className="fwd-avatar" style={{ background: getColor(name) }}>
                  {c.contact?.avatar_url
                    ? <img src={c.contact.avatar_url} alt="" className="fwd-avatar-img" />
                    : getInitial(c)
                  }
                </div>
                <div className="fwd-info">
                  <span className="fwd-name">{name}</span>
                  <span className="fwd-phone">{phone}</span>
                </div>
                <div className="fwd-status">
                  {isSending && <div className="fwd-spinner" />}
                  {isSent && (
                    <div className="fwd-check">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {sent.length > 0 && (
          <div className="fwd-footer">
            <button className="fwd-done" onClick={onClose}>Concluído</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatWindow;
